import { Component, EventEmitter, Input, OnInit, Output, signal, ViewChild, WritableSignal, } from '@angular/core';
import { ExpandableAreaComponent, SohoBusyIndicatorDirective, SohoSearchFieldComponent } from 'ids-enterprise-ng';
import { LMBuilderService, LMFacadeService } from "@app/components";
import { LMBusyIndicatorService, LMErrorUtilService } from "@app/services";
import { LMBuilderModel } from "@components/builder/services/lm-builder-model.service";

@Component({
  selector: 'lm-builder-palette',
  standalone: false,
  templateUrl: './lm-builder-palette.component.html',
  styleUrl: './lm-builder-palette.component.scss',
})

export class LMBuilderPaletteComponent implements OnInit {
  @ViewChild(SohoBusyIndicatorDirective) busyIndicator: SohoBusyIndicatorDirective | undefined;
  @ViewChild(SohoSearchFieldComponent) searchField: SohoSearchFieldComponent | undefined;
  @ViewChild(ExpandableAreaComponent) expandableArea: ExpandableAreaComponent | undefined;

  @Input() businessClass: string | undefined;
  @Input() facade: LMBusinessClassFacade | undefined;
  @Input() moduleTag: string | undefined;
  @Input() canAdd = true;
  @Input() addTooltip = '';
  @Input() dragTooltip = '';
  @Input() dropTargets: Array<string> = [];
  @Input() mode: LMBuilderFieldPaletteMode = 'fields';
  
  @Output() advancedSearch: EventEmitter<string> = new EventEmitter<string>();
  @Output() addField: EventEmitter<LMBuilderField> = new EventEmitter<LMBuilderField>();

  protected readonly Soho = Soho;
  protected readonly LMBuilderModel = LMBuilderModel;
  errorHeader = Soho.Locale.translate('UnexpectedErrorOccured');
  hasError = false;
  errorMessage = '';

  hasParentChain: WritableSignal<boolean> = signal(false);
  parentPath: WritableSignal<string | undefined> = signal(undefined);
  searchValue = signal('');
  searchPlaceholder = signal(Soho.Locale.translate('Search'));
  fields: WritableSignal<Array<LMBuilderField> | undefined> = signal(undefined);
  relatedFields: WritableSignal<Array<LMBuilderField> | undefined> = signal(undefined);
  searchResults: WritableSignal<Array<LMBuilderField> | undefined> = signal(undefined);
  paneOpen = signal(true);
  relatedFieldsButtonIcon = signal('chevron-up');
  paneCollapseButton = signal('collapse-app-tray');
  private counter = 0;
  private relatedFieldsOpen = true;
  private parentChain: Array<LMBuilderField> = [];
  
  constructor(
      private lmBusyIndicatorService: LMBusyIndicatorService,
      private lmErrorUtilService: LMErrorUtilService,
      private lmFacadeService: LMFacadeService,
      private lmBuilderService: LMBuilderService) {
  }

  ngOnInit(): void {
    this.handleFacadeResponse(this.facade);
  }

  private fetchGroupFieldFacade(businessClass: string, groupField: string) {
    this.lmBusyIndicatorService.showBusyIndicator(this.busyIndicator);
    this.lmFacadeService.getGroupFieldFacade(businessClass, groupField).subscribe({
      next:  (facade)       => { this.handleGroupFieldFacadeResponse(facade);   },
      error: (e)            => { this.handleError(e);                           }
    });
  }

  private handleGroupFieldFacadeResponse(facade: LMBusinessClassFacade) {
    this.lmBusyIndicatorService.hideBusyIndicator(this.busyIndicator);
    this.facade = facade;
    this.buildFields(facade);
    this.relatedFields.set([]);
  }

  private fetchFacade(businessClass: string) {
      this.lmBusyIndicatorService.showBusyIndicator(this.busyIndicator);
      this.lmFacadeService.getBusinessClassFacade(businessClass).subscribe({
        next:  (facade)       => { this.handleFacadeResponse(facade);   },
        error: (e)            => { this.handleError(e);                 }
      });
  }

  private handleFacadeResponse(facade: LMBusinessClassFacade | undefined) {
    this.lmBusyIndicatorService.hideBusyIndicator(this.busyIndicator);

    if (this.lmBuilderService.responseHasError(facade)) {
      this.handleError(facade);
      return;
    }

    if (facade) {
      this.facade = facade;
      this.buildFields(facade);
      this.buildRelatedFields(facade);
    }
  }

  doTextSearch() {
    this.searchResults.set(this.buildSearchResults());
  }

  clearTextSearch() {
    this.searchValue.set('');
    this.searchResults.set(undefined);
  }

  private buildSearchResults(): Array<LMBuilderField> {
    const searchResults = new Array<LMBuilderField>;
    const searchTerm = this.searchValue().toLowerCase();
    this.fields()?.forEach(builderField => {
      if (this.isSearchHit(searchTerm, builderField)) {
        searchResults.push(builderField);
      }
    });

    this.relatedFields()?.forEach(builderField => {
      if (this.isSearchHit(searchTerm, builderField)) {
        searchResults.push(builderField);
      }
    });

    searchResults.sort((a, b) =>  a.label.localeCompare(b.label));
    return searchResults;
  }

  private isSearchHit(searchTerm: string, builderField: LMBuilderField): boolean {
    if (searchTerm) {
      if (searchTerm.length > 2 ) {
        return builderField.label.toLowerCase().indexOf(searchTerm) > -1;
      } else {
        return builderField.label.toLowerCase().indexOf(searchTerm) === 0;
      }
    }

    return false;
  }

  private buildRelatedFields(facade: LMBusinessClassFacade) {
    const builderRelatedFields = new Array<LMBuilderField>;
    const module = this.moduleTag ? this.moduleTag : '';

    if (facade && facade.relations) {
      const facadeRelations: Array<LMFacadeRelation> = this.lmFacadeService.getFacadeRelations(module, facade);
      facadeRelations.forEach(facade => {
        const field: LMBuilderField = {
          uniqueId: this.counter++,
          parent: this.getLastParent(),
          facadeType: 'relation',
          facade: facade,
          label: facade.name,
          tooltip: this.buildRelatedTooltip(facade),
          description: facade.description,
          isSymbolicKey: false,
          type: facade.isOTMLink ? 'One To Many' : 'One To One',
          icon: 'info'
        };

        builderRelatedFields.push(field);
      });
    }
    this.relatedFields.set(builderRelatedFields);
  }

  private buildFields(facade: LMBusinessClassFacade) {
    const builderFields = new Array<LMBuilderField>;
    const module = this.moduleTag ? this.moduleTag  : '';
    const lastParent = this.getLastParent();
    const lastParentKeyField = this.isBuilderFieldKeyField(lastParent);

    if (facade && facade.fields) {
      const facadeFields: Array<LMFacadeField> = this.lmFacadeService.getFacadeFields(module, facade);
      facadeFields.forEach(facade => {
        if (!lastParentKeyField || (lastParentKeyField && !facade.uiFieldSpec.isSymbolicKey)) {
          builderFields.push(this.buildField(facade));
        }
      });
    }

    if (facade && facade.conditions) {
      const facadeFields: Array<LMFacadeField> = this.lmFacadeService.getFacadeConditions(module, facade);
      facadeFields.forEach(facade => {
        builderFields.push(this.buildCondition(facade));
      });
    }

    this.fields.set(builderFields);
  }

  private isBuilderFieldKeyField(builderField: LMBuilderField | undefined): boolean {
    if (!builderField) {
      return false;
    }

    if (builderField.facadeType !== 'field') {
      return false;
    }

    const facadeField = builderField.facade as LMFacadeField;
    return facadeField.uiFieldSpec.isKeyField ? true : false;
  }

  private buildField(facade: LMFacadeField): LMBuilderField {
    return  {
      uniqueId: this.counter++,
      parent: this.getLastParent(),
      facade: facade,
      facadeType: 'field',
      label: this.buildFieldLabel(facade),
      tooltip: this.buildFieldTooltip(facade),
      description: facade.description,
      isSymbolicKey: facade.uiFieldSpec.isSymbolicKey ? true : false,
      type: this.getBuilderFieldTypeTooltip(facade),
      icon: this.getBuilderFieldIcon(facade)
    };
  }

  private buildCondition(facade: LMFacadeField): LMBuilderField {
    return  {
      uniqueId: this.counter++,
      parent: this.getLastParent(),
      facadeType: 'condition',
      facade: facade,
      label: this.buildFieldLabel(facade),
      tooltip: this.buildFieldTooltip(facade),
      description: facade.description,
      isSymbolicKey: false,
      type: this.getBuilderFieldTypeTooltip(facade),
      icon: this.getBuilderFieldIcon(facade)
    };
  }

  private buildFieldLabel(facadeField: LMFacadeField): string {
    if (facadeField.label) {
      if (facadeField.name && facadeField.name.trim() !== facadeField.label.trim() ) {
        return `${facadeField.label} (${facadeField.name})`;
      }
      return facadeField.label;
    }

    if (facadeField.name) {
      return facadeField.name;
    }

    if (facadeField?.uiFieldSpec.label) {
      return facadeField.uiFieldSpec.label;
    }

    return facadeField.fieldId;
  }

  private buildFieldTooltip(facade: LMFacadeField): string {
    const labelStyle = `style="font-size: 1.4rem; opacity: .8; text-align: left; padding-bottom: 2px"`;
    const fieldStyle = `style="font-size: 1.4rem; text-align: left; overflow: hidden; text-overflow: ellipsis; padding-bottom: 12px"`;
    const lplStyle = `style="font-size: 1.4rem; text-align: left; overflow: hidden; text-overflow: ellipsis; padding-bottom: 12px; white-space: pre"`;
    const stateStyle = `style="font-size: 1.4rem; text-align: left; overflow: hidden; text-overflow: ellipsis; padding-bottom: 2px"`;

    const description = facade.description ? `<div ${fieldStyle}>${facade.description}</div>` : '';
    const businessCLass = `<div ${labelStyle}>${Soho.Locale.translate('BusinessClass')}</div><div ${fieldStyle}>${facade.uiFieldSpec.businessClass}</div>`;
    const label = facade.label ? `<div ${labelStyle}>${Soho.Locale.translate('ListLabel')}</div><div ${fieldStyle}>${facade.label}</div>`: '';
    const fieldId = `<div ${labelStyle}>${Soho.Locale.translate('FieldID')}</div><div ${fieldStyle}>${facade.fieldId}</div>`;
    const stateList: Array<string> = [];

    if (facade.uiFieldSpec.isStateField && facade.uiFieldSpec.states) {
      stateList.push(`<div ${labelStyle}>${Soho.Locale.translate('States')}</div>`);
      facade.uiFieldSpec.states.filter(state => {
        stateList.push(`<div ${stateStyle}>${state.label} = ${state.value}</div>`);
      });
    }
    const states = stateList.join('');
    const lpl = facade.uiFieldSpec && facade.uiFieldSpec.lpl ? `<div ${labelStyle}>${Soho.Locale.translate('DefinitionLabel')}</div><div ${lplStyle}>${facade.uiFieldSpec.lpl}</div>` : '';

    return `${description}${businessCLass}${label}${fieldId}${states}${lpl}`;
  }

  private buildRelatedTooltip(facade: LMFacadeRelation): string {
    const labelStyle = `style="font-size: 1.4rem; opacity: .8; text-align: left; padding-bottom: 2px"`;
    const fieldStyle = `style="font-size: 1.4rem; text-align: left; overflow: hidden; text-overflow: ellipsis; padding-bottom: 12px"`;
    const lplStyle = `style="font-size: 1.4rem; text-align: left; overflow: hidden; text-overflow: ellipsis; padding-bottom: 12px; white-space: pre"`;

    const description = facade.description ? `<div ${fieldStyle}>${facade.description}</div>` : '';
    const businessCLass = `<div ${labelStyle}>${Soho.Locale.translate('BusinessClass')}</div><div ${fieldStyle}>${facade.lastBusinessClass}</div>`;
    const relatedLink = `<div ${labelStyle}>${facade.isOTMLink ? Soho.Locale.translate('OneToManyFields') : Soho.Locale.translate('OneToOneFields')}</div><div ${fieldStyle}>${facade.relatedLink}</div>`;
    const lpl = facade.lpl ? `<div ${labelStyle}>${Soho.Locale.translate('DefinitionLabel')}</div><div ${lplStyle}>${facade.lpl}</div>` :'';
    return `${description}${businessCLass}${relatedLink}${lpl}`;
  }

  private getBuilderFieldTypeTooltip(facadeField: LMFacadeField): string {
    if (facadeField.uiFieldSpec.isKeyField) {
      return 'Key Field';
    }

    if (facadeField.uiFieldSpec.fieldType === 'GROUP') {
      return 'Group';
    }

    switch (facadeField.uiFieldSpec.lmrkFieldType ) {
      case 'BOOLEAN':
        return 'True or False';
      case 'BCD':
      case 'NUMERIC':
      case 'PERCENT':
        return 'Number';
      case 'TIMESTAMP':
      case 'YYYYMMDD':
      case 'YYYYMM':
      case 'YYYYWW':
      case 'YYYYQ':
      case 'YYYY':
      case 'MMDD':
      case 'TIME':
        return 'Date or Time';
      default:
        return 'Text';
    }
  }

  private getBuilderFieldIcon(facadeField: LMFacadeField): string {
    if (facadeField.uiFieldSpec.isKeyField) {
      return 'key';
    }
    switch (facadeField.uiFieldSpec.lmrkFieldType ) {
      case 'BOOLEAN':
        return 'check-list';
      case 'BCD':
      case 'NUMERIC':
      case 'PERCENT':
          return 'number';
      case 'TIMESTAMP':
      case 'YYYYMMDD':
      case 'YYYYMM':
      case 'YYYYWW':
      case 'YYYYQ':
      case 'YYYY':
      case 'MMDD':
      case 'TIME':
            return 'calendar';
      default:
        return 'change-font';
    }
  }

  private handleError(error: any) {
      this.hasError = true;
      this.errorMessage = error.message;
      this.lmBusyIndicatorService.hideBusyIndicator(this.busyIndicator);
      this.lmErrorUtilService.handleUnexpectedError(error);
  }

  onAdvancedSearchClicked(): void {
    this.advancedSearch.emit();
  }

  toggleHeaders($event: Event): void {
    $event.stopPropagation();
    $event.stopImmediatePropagation();
    if (this.expandableArea) {
      if (this.relatedFieldsOpen) {
        this.expandableArea.close();
        this.relatedFieldsOpen = false;
      } else {
        this.expandableArea.open();
        this.relatedFieldsOpen = true;
      }
      this.setIcon();
    }
  }

  setIcon(): void {
    if (this.relatedFieldsOpen) {
      this.relatedFieldsButtonIcon.set('chevron-up');
    } else {
      this.relatedFieldsButtonIcon.set('chevron-down');
    }
  }

  toggleCollapse(): void {
   if (this.paneOpen()) {
     this.paneCollapseButton.set('double-chevron');
     this.paneOpen.set(false);
   } else {
     this.paneCollapseButton.set('collapse-app-tray');
     this.paneOpen.set(true);
   }
  }

  handleAddField(builderField: LMBuilderField) {
    this.addField.emit(builderField);
  }

  removeLastParent() {
    if (this.parentChain.length > 0) {
      this.parentChain.pop();
      this.updateParentPath();
    }
    this.hasParentChain.set(this.parentChain.length > 0);
    this.fetchChildren();
  }

  addParent(builderField: LMBuilderField) {
    this.parentChain.push(builderField);
    this.updateParentPath();
    this.hasParentChain.set(true);
    this.fetchChildren();
  }

  private fetchChildren() {
    this.clearTextSearch();
    const builderField = this.getLastParent();
    const businessClass = this.getLastParentBusinessClass(builderField);
    const isGroupField = this.isLastParentGroupField(builderField);

    if (businessClass) {
      if (isGroupField) {
        const groupFieldFacade = builderField?.facade as LMFacadeField;
        this.fetchGroupFieldFacade(businessClass, groupFieldFacade.uiFieldSpec.fieldName);
      } else {
        this.fetchFacade(businessClass);
      }
    }
  }

  private updateParentPath() {
    let label = '';

    if (this.parentChain.length > 0) {
      this.parentChain.forEach(builderField => {
        label = label.length > 0 ? `${label}.${builderField.label}` :  builderField.label;
      });

      this.parentPath.set(label);
    } else {
      this.parentPath.set(undefined);
    }
  }

  private getLastParentBusinessClass(builderField: LMBuilderField | undefined): string | undefined {
    if (builderField) {
      if (builderField.facadeType === 'field') {
        const facadeField = builderField.facade as LMFacadeField;
        return facadeField.uiFieldSpec.fieldType === 'GROUP' ? facadeField.uiFieldSpec.businessClass : facadeField.uiFieldSpec.relatedBusinessClass;
      }
      if (builderField.facadeType === 'relation') {
        const facadeRelation = builderField.facade as LMFacadeRelation;
        return facadeRelation.lastBusinessClass;
      }
    } else  if (this.businessClass) {
      return this.businessClass;
    }

    return undefined;
  }

  private isLastParentGroupField(builderField: LMBuilderField | undefined): boolean {
    if (builderField) {
      if (builderField.facadeType === 'field') {
        const facadeField = builderField.facade as LMFacadeField;
        return facadeField.uiFieldSpec?.fieldType === 'GROUP';
      }
    }

    return false;
  }

  private getLastParent(): LMBuilderField | undefined {
    return this.parentChain.length > 0 ? this.parentChain[this.parentChain.length - 1] : undefined;
  }
}
